import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import { getLocales } from 'assets/locales/Locale';
import ErrorPage from 'assets/templates/errorPage/ErrorPage';
import { HttpStatus, claimsHomeRoute } from 'config/Api.Config';
import ClaimCommentType from 'models/enums/ClaimCommentType';
import ClaimChangeOwnership from 'module/claims/components/claimsChangeOwnership/Claim.ChangeOwnership';
import ClaimDownloadDocuments from 'module/claims/components/claimsDownloadDocuments/Claim.DownloadDocuments';
import ClaimStatusStateMachine from 'module/claims/components/claimsStateMachine/Claim.StatusStateMachine';
import { ClaimEditProvider } from './ClaimEdit.Hooks';
import ClaimEditAlerts from './sections/ClaimEdit.Alerts';
import ClaimEditClaimHistory from './sections/ClaimEdit.ClaimHistory';
import ClaimEditComments from './sections/ClaimEdit.Comments';
import ClaimEditDocuments from './sections/ClaimEdit.Documents';
import ClaimEditInvoices from './sections/ClaimEdit.Invoices';
import ClaimEditJobs from './sections/ClaimEdit.Jobs';
import ClaimEditLogs from './sections/ClaimEdit.Logs';
import ClaimEditOwnershipLogs from './sections/ClaimEdit.OwnershipLogs';
import ClaimEditStatusAuditLogs from './sections/ClaimEdit.StatusAuditLogs';
import ClaimEditTabs from './sections/ClaimEdit.Tabs';
import ClaimEditEmails from './sections/claimEditEmail/ClaimEdit.Emails';
import ClaimEditEmailTemplate from './sections/claimEditEmail/ClaimEdit.EmailTemplate';
import ClaimEditSendEmail from './sections/claimEditEmail/ClaimEdit.SendEmail';
import ClaimEditInfo from './sections/claimEditInfo/ClaimEdit.Info';
import { ClaimJobEditProvider } from './sections/claimJobEdit/ClaimEdit.Job.Hooks';
import ClaimEditJobForm from './sections/claimJobEdit/claimJobForm/ClaimEdit.Job.Form';
import ThemePageCentered from 'assets/components/page/themes/Theme.Page.Centered';
import { combineStrings } from 'assets/utils/data/String';
import LinkButton from 'assets/components/button/LinkButton';
import { compact } from 'lodash';
import Box from 'assets/components/box/Box';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';
import { Fragment } from 'react/jsx-runtime';

export const claimEditIndexRoute = claimsHomeRoute + '/claim-edit/:claimId?/:jobId?';

export default function ClaimEditIndex() {
  const { lang } = getLocales();
  return (
    <ClaimEditProvider
      render={({
        hasReports,
        hasAnyPermission,
        isLoading,
        emailTemplates,
        statusInfo,
        isStatusChanged,
        currentStatus,
        claim,
        product,
        contract,
        changeStatusForm,
        permissions,
        emailAction,
        setEmailAction,
        isClaimAdmin,
        reloadClaim,
        currentUserPermissions,
        canViewClaimAlertNotifications,
        isClaimJobEditableByStatus,
        hasAnyClaimCommentPermission,
        hasAnyClaimDocumentPermission,
        hasAnyClaimInfoPermission,
        hasAnyClaimInvoicePermission,
        hasAnyClaimJobPermission,
        isClaimOwner,
        lastModifiedJob,
        findClaimApi,
        hasRequestedClaim,
      }) => {
        const isJobAddable =
          isClaimJobEditableByStatus || hasReports || !(isClaimAdmin || (isClaimOwner && permissions.CREATE_CLAIMJOB));
        const canChangeOwner = isClaimJobEditableByStatus || !(isClaimAdmin || permissions?.UPDATE_CLAIM_OWNER);
        const canTakeOwnership = isClaimJobEditableByStatus || !permissions?.UPDATE_CLAIM_OWNER;
        const canChangeStatus = isClaimAdmin || (isClaimOwner && permissions?.UPDATE_CLAIM_STATUS);
        const canSendEmail = (isClaimOwner && permissions?.SEND_CLAIMEMAIL) || isClaimAdmin;
        const canDownload = (isClaimOwner && permissions?.DOWNLOAD_AllCLAIMDOCUMENT) || isClaimAdmin;

        const isLoadingClaim = !hasRequestedClaim || isLoading || findClaimApi.isExecuting;
        const isError = hasRequestedClaim && !isLoading && !findClaimApi.isExecuting && !claim;
        const isDisabled =
          !(permissions?.CLAIM_ADMIN || permissions?.VIEW_CLAIM_CONTRACTINFO) || !contract?.documentationAggregate?.url;

        if (!hasAnyPermission) {
          return <ErrorPage status={HttpStatus.unauthorized} />;
        }

        if (isError) {
          return <ErrorPage status={findClaimApi.status} message={findClaimApi.message} />;
        }

        return (
          <Page
            class={ThemePageCentered}
            isLoading={isLoadingClaim}
            htmlElementProps={{
              pageHeader: { id: 'info' },
            }}
            style={{
              page: {
                '--pageWidth': '98%',
              } as React.CSSProperties,
            }}
            header={
              isLoading || findClaimApi.isExecuting || !claim ? null : (
                <>
                  <h2>
                    {!claim?.contract ? (
                      lang.noContractDefined
                    ) : (
                      <>{claim?.number ?? combineStrings('-', product?.category?.code, claim?.id?.toString())}</>
                    )}
                  </h2>
                </>
              )
            }
            subHeader={() =>
              isLoading || findClaimApi.isExecuting || !claim ? null : (
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: '10px',
                    marginBottom: '10px',
                    gap: '5px',
                  }}
                >
                  <ClaimEditTabs />
                  <Box class={ThemeWrapperStack}>
                    {claim && (
                      <ClaimJobEditProvider
                        render={({ setAction, action }) => (
                          <>
                            {action === 'edit' && <ClaimEditJobForm />}
                            <Button
                              class={ThemeButtonCircle}
                              media={<Icon class="fas-plus" />}
                              render={lang.newJob}
                              onClick={setAction}
                              data="edit"
                              disabled={isJobAddable}
                              htmlElementProps={{ title: `${lang.add} ${lang.newJob}` }}
                            />
                          </>
                        )}
                      />
                    )}
                    <ClaimStatusStateMachine
                      claim={claim}
                      emailTemplates={emailTemplates}
                      statusInfo={statusInfo}
                      disabled={!canChangeStatus}
                      isStatusChanged={isStatusChanged}
                      ownerId={changeStatusForm.data?.ownerId}
                      currentStatus={currentStatus}
                      changeStatus={changeStatusForm}
                      setEmailAction={setEmailAction}
                    />
                    <ClaimChangeOwnership
                      emailTemplates={emailTemplates}
                      statusInfo={statusInfo}
                      canTakeOwnership={canTakeOwnership}
                      hasPermission={canChangeOwner}
                      claim={claim}
                      currentPermissions={currentUserPermissions}
                      reloadClaim={reloadClaim}
                    />
                    <ClaimDownloadDocuments claim={claim} isDisabled={!canDownload} />
                    <Button
                      class={ThemeButtonCircle}
                      media={<Icon class="fas-envelope" />}
                      render={lang.sendEmail}
                      onClick={setEmailAction}
                      data="sendEmail"
                      htmlElementProps={{ title: lang.sendEmail }}
                      disabled={!canSendEmail}
                    />
                    {emailAction === 'sendEmail' && <ClaimEditSendEmail />}
                    {/* <ClaimEditSubmitButton /> */}
                  </Box>
                </div>
              )
            }
            render={compact([
              isLoading || findClaimApi.isExecuting || !claim ? null : (
                <Fragment key="generalInfo">
                  {claim?.id ? (
                    <p>
                      <span>
                        {lang.claimStatus}:{' '}
                        {claim?.statusCode ? lang[claim?.statusCode]?.replace(/_/g, ' - ') : lang.noStatusDefined}
                      </span>
                      <br />
                      <span>
                        {lang.lastJobStatus}:{' '}
                        {lastModifiedJob?.statusCode
                          ? lang[lastModifiedJob?.statusCode]?.replace(/_/g, ' - ')
                          : lang.noStatusDefined}
                      </span>
                    </p>
                  ) : (
                    <p>{lang.newRepairVisit}</p>
                  )}
                  <LinkButton
                    class={ThemeButtonCircle}
                    url={contract?.documentationAggregate?.url}
                    disabled={isDisabled}
                    media={<Icon class="fas-download" />}
                    target="_blank"
                  >
                    {`${lang.download} ${lang.contractDocument} ${contract?.appNumber}`}
                  </LinkButton>
                </Fragment>
              ),
              changeStatusForm.data?.emailTemplate && emailAction === 'changeStatusSendEmail' && (
                <ClaimEditEmailTemplate key="emailTemplate" />
              ),
              (permissions?.VIEW_CLAIM_HISTORY || isClaimAdmin) && <ClaimEditClaimHistory key="history" />,
              canViewClaimAlertNotifications && <ClaimEditAlerts key="alerts" />,
              hasAnyClaimInfoPermission && <ClaimEditInfo key="info" />,
              hasAnyClaimJobPermission && <ClaimEditJobs key="jobs" />,
              hasAnyClaimCommentPermission && (
                <ClaimEditComments commentType={ClaimCommentType.external} key="externalComments" />
              ),
              hasAnyClaimDocumentPermission && <ClaimEditDocuments key="documents" />,
              hasAnyClaimInvoicePermission && <ClaimEditInvoices key="invoices" />,
              (permissions?.VIEW_CLAIM_LOGS || isClaimAdmin) && <ClaimEditOwnershipLogs key="ownershipLogs" />,
              (permissions?.VIEW_CLAIMEMAIL_HISTORY || isClaimAdmin) && <ClaimEditEmails key="emails" />,
              (permissions?.VIEW_CLAIM_LOGS || isClaimAdmin) && <ClaimEditStatusAuditLogs key="auditLogs" />,
              (permissions?.VIEW_CLAIM_LOGS || isClaimAdmin) && <ClaimEditLogs key="logs" />,
            ])}
          />
        );
      }}
    />
  );
}
